import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import '../assets/CreateClaimModal.css';

const CreateClaimModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    amount: '',
    reason: '',
    type: '', // Don't set default type until we fetch from API
    itemName: '',
    purchaseDate: '',
    affectsOtherProfitSharers: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [claimTypes, setClaimTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);

  // Only profit sharers (co-founders) can toggle the shared cost option.
  // Regular tutors' claims always deduct from profit sharers.
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isProfitSharer = !!user.isCoFounder;

  // Fetch claim types on component mount
  useEffect(() => {
    fetchClaimTypes();
  }, []);

  const fetchClaimTypes = async () => {
    try {
      setTypesLoading(true);
      const response = await adminApi.getClaimTypes();
      const types = response.data.data;
      setClaimTypes(types);
      
      // Set default type to first available type
      if (types && types.length > 0 && !formData.type) {
        setFormData(prev => ({ ...prev, type: types[0].value }));
      }
    } catch (error) {
      console.error('Error fetching claim types:', error);
      setError('Failed to load claim types. Please refresh and try again.');
    } finally {
      setTypesLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(''); // Clear general error when user starts typing
    setFieldErrors(prev => ({ ...prev, [field]: '' })); // Clear field-specific error
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      errors.amount = 'Amount must be greater than 0';
    }

    if (parseFloat(formData.amount) > 100) {
      errors.amount = 'Amount cannot exceed $100';
    }

    if (!formData.reason || formData.reason.trim().length === 0) {
      errors.reason = 'Reason is required';
    }

    if (formData.reason.length > 500) {
      errors.reason = 'Reason cannot exceed 500 characters';
    }

    if (!formData.type) {
      errors.type = 'Type is required';
    }

    if (!formData.itemName || formData.itemName.trim().length === 0) {
      errors.itemName = 'Item name is required';
    }

    if (!formData.purchaseDate) {
      errors.purchaseDate = 'Purchase date is required';
    } else {
      const purchaseDate = new Date(formData.purchaseDate);
      const now = new Date();
      
      if (purchaseDate > now) {
        errors.purchaseDate = 'Purchase date cannot be in the future';
      }
      
      // Check if purchase date is more than 12 months ago
      const twelveMonthsAgo = new Date();
      twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
      
      if (purchaseDate < twelveMonthsAgo) {
        errors.purchaseDate = 'Purchase date cannot be more than 12 months ago';
      }
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setError('Please fix the errors below');
      return;
    }

    setLoading(true);
    setError('');
    setFieldErrors({});

    try {
      // Derive month and year directly from the YYYY-MM-DD string so the
      // claim always files under the SGT calendar month the user picked,
      // regardless of the browser's timezone.
      const [yStr, mStr] = formData.purchaseDate.split('-');
      const year = parseInt(yStr, 10);
      const month = parseInt(mStr, 10);

      const claimData = {
        amount: parseFloat(formData.amount),
        reason: formData.reason.trim(),
        type: formData.type,
        month: month,
        year: year,
        itemName: formData.itemName.trim(),
        purchaseDate: formData.purchaseDate,
        affectsOtherProfitSharers: formData.affectsOtherProfitSharers
      };

      const response = await adminApi.createClaim(claimData);
      onSuccess(response.data.data);
    } catch (error) {
      console.error('Error creating claim:', error);
      setError(error.response?.data?.message || error.message || 'Failed to create claim');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getMonthName = (month) => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[month - 1] || '';
  };

  const getCharacterCount = () => {
    return formData.reason.length;
  };

  const getCharacterCountClass = () => {
    const count = getCharacterCount();
    if (count > 450) return 'error';
    if (count > 400) return 'warning';
    return '';
  };

  return (
    <div className="create-claim-modal ccm-overlay" onClick={handleOverlayClick}>
      <div className="ccm-content">
        <div className="ccm-header">
          <h2 className="ccm-title">Submit New Claim</h2>
          <button 
            className="ccm-close" 
            onClick={onClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="ccm-form">
          <div className="ccm-body">
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="instructions-section">
              <div className="instructions-title">Important Instructions:</div>
              <ul className="instructions-list">
                <li>Ensure you've discussed with the admin and got their agreement before submitting this form</li>
                <li>Claim amount, if approved, will be added into this month's invoice - please check</li>
              </ul>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="required">Type</label>
                <div className={fieldErrors.type ? 'field-with-error' : ''}>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    disabled={loading || typesLoading}
                    className={fieldErrors.type ? 'error' : ''}
                  >
                    {typesLoading ? (
                      <option value="">Loading types...</option>
                    ) : (
                      <>
                        <option value="">Select a type</option>
                        {claimTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {fieldErrors.type && (
                    <div className="field-error-tooltip">{fieldErrors.type}</div>
                  )}
                </div>
                {typesLoading && (
                  <div className="form-help">Loading claim types...</div>
                )}
              </div>

              <div className="form-group">
                <label className="required">Amount</label>
                <div className={fieldErrors.amount ? 'field-with-error' : ''}>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max="100"
                    value={formData.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    placeholder="0.00"
                    disabled={loading}
                    className={fieldErrors.amount ? 'error' : ''}
                  />
                  {fieldErrors.amount && (
                    <div className="field-error-tooltip">{fieldErrors.amount}</div>
                  )}
                </div>
                <div className="form-help">
                  Maximum amount: $100
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="required">Item Name</label>
                <div className={fieldErrors.itemName ? 'field-with-error' : ''}>
                  <input
                    type="text"
                    value={formData.itemName}
                    onChange={(e) => handleInputChange('itemName', e.target.value)}
                    placeholder="Enter item name"
                    disabled={loading}
                    maxLength={100}
                    className={fieldErrors.itemName ? 'error' : ''}
                  />
                  {fieldErrors.itemName && (
                    <div className="field-error-tooltip">{fieldErrors.itemName}</div>
                  )}
                </div>
                <div className="form-help">
                  Name of the item/service being claimed
                </div>
              </div>

              <div className="form-group">
                <label className="required">Purchase Date</label>
                <div className={fieldErrors.purchaseDate ? 'field-with-error' : ''}>
                  <input
                    type="date"
                    value={formData.purchaseDate}
                    onChange={(e) => handleInputChange('purchaseDate', e.target.value)}
                    disabled={loading}
                    className={fieldErrors.purchaseDate ? 'error' : ''}
                  />
                  {fieldErrors.purchaseDate && (
                    <div className="field-error-tooltip">{fieldErrors.purchaseDate}</div>
                  )}
                </div>
                <div className="form-help">
                  Date when the item was purchased (within last 12 months)
                </div>
              </div>
            </div>

            <div className="form-group">
              <div className="label-with-tooltip">
                <label className="required">Reason</label>
                <div className="tooltip-container">
                  <div className="tooltip-icon">?</div>
                  <div className="tooltip-text">
                    Please explain the reason for this claim
                  </div>
                </div>
              </div>
              <div className={fieldErrors.reason ? 'field-with-error' : ''}>
                <textarea
                  value={formData.reason}
                  onChange={(e) => handleInputChange('reason', e.target.value)}
                  placeholder="Please provide a detailed explanation for this claim..."
                  disabled={loading}
                  maxLength={500}
                  className={fieldErrors.reason ? 'error' : ''}
                />
                {fieldErrors.reason && (
                  <div className="field-error-tooltip">{fieldErrors.reason}</div>
                )}
              </div>
              <div className={`character-counter ${getCharacterCountClass()}`}>
                {getCharacterCount()}/500 characters
              </div>
            </div>

            {/* Only profit sharers can toggle shared cost — regular tutors' claims always affect profit sharers */}
            {isProfitSharer && (
            <div className="form-group profit-share-toggle">
              <div className="label-with-tooltip">
                <label>Affects Other Profit Sharers</label>
                <div className="tooltip-container">
                  <div className="tooltip-icon">?</div>
                  <div className="tooltip-text">
                    When ON (default): the approved amount is cost-shared — other profit sharers will each pay their proportional share of this claim.<br /><br />
                    When OFF: this is a personal adjustment. The full amount is added to your invoice only, and other profit sharers are not affected.
                  </div>
                </div>
              </div>
              <div className="profit-share-toggle-row">
                <div className="profit-share-toggle-text">
                  <small className="form-help">
                    {formData.affectsOtherProfitSharers
                      ? 'Cost will be shared with other profit sharers (default)'
                      : 'Personal — other profit sharers will NOT be affected'}
                  </small>
                </div>
                <label className="ccm-toggle">
                  <input
                    type="checkbox"
                    checked={formData.affectsOtherProfitSharers}
                    onChange={(e) => handleInputChange('affectsOtherProfitSharers', e.target.checked)}
                    disabled={loading}
                  />
                  <span className="ccm-toggle-track">
                    <span className="ccm-toggle-thumb"></span>
                  </span>
                </label>
              </div>
            </div>
            )}
          </div>

          <div className="ccm-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading && <span className="loading-spinner-small"></span>}
              {loading ? 'Submitting...' : 'Submit Claim'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateClaimModal;